import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { getWricefCatalog } from "@/api/wricef-catalog";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import {
  UNASSIGNED_MODULE,
  moduleBuckets,
} from "@/components/projects/diagnostic/diagnosticData";
import { FilterButton } from "@/components/projects/diagnostic/orgStructure/FilterButton";
import { formatCount } from "@/lib/format";

/** Teto da página: a lista é para ler na reunião, e o filtro por módulo a encurta. */
const PAGE = 500;

const HEAD = "px-3 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-3 py-2 align-top text-[#08254c]";

/**
 * "Customizações por módulo" do Ato 2, objeto a objeto (pedido do David em
 * 19/10/2026, antes da apresentação da AL5).
 *
 * O card diz onde o código Z se concentra, e a pergunta seguinte do cliente é
 * "quais são". O detalhe usa a linguagem do card, filtra pelo mesmo eixo (o código
 * do componente de aplicação, e o balde de quem não tem módulo atribuível) e lê a
 * lista do catálogo, cujo filtro `stream` aplica a mesma regra do card. Os
 * números dos filtros são os do card; a lista é a do catálogo de hoje. Sem valor
 * financeiro.
 */
export function ModuleCustomizationsDetail({ sections }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const [search, setSearch] = useSearchParams();
  const { buckets, unidentified, identified } = moduleBuckets(sections);
  const known = new Set([...buckets.map((bucket) => bucket.code), UNASSIGNED_MODULE]);
  const selected = search.get("module");
  const module = selected && known.has(selected) ? selected : null;

  const choose = (code: string | null) => {
    const next = new URLSearchParams(search);
    if (code) next.set("module", code);
    else next.delete("module");
    setSearch(next, { replace: true });
  };

  const list = useQuery({
    queryKey: ["diagnostic", "module-customizations", projectId, module],
    queryFn: () =>
      getWricefCatalog(projectId, {
        ...(module ? { stream: [module] } : {}),
        limit: PAGE,
        offset: 0,
      }),
    enabled: !!projectId,
    retry: false,
  });
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  return (
    <div className="space-y-4">
      {identified > 0 && (
        <p className="text-[13px] text-[#596b85]">
          {t("Where the Z code sits · {{identified}} with an identified module", {
            identified: formatCount(identified),
          })}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-[#e3e8ef] bg-white">
        <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] px-4 py-3">
          <FilterButton active={module === null} onClick={() => choose(null)}>
            {t("All")}
          </FilterButton>
          {buckets.map((bucket) => (
            <FilterButton
              key={bucket.code}
              active={module === bucket.code}
              onClick={() => choose(bucket.code)}
            >
              {bucket.labelled
                ? bucket.label
                : t("Code {{code}} (no label)", { code: bucket.label })}{" "}
              {formatCount(bucket.count)}
            </FilterButton>
          ))}
          {unidentified > 0 && (
            <FilterButton
              active={module === UNASSIGNED_MODULE}
              onClick={() => choose(UNASSIGNED_MODULE)}
            >
              {t("No assignable module")} {formatCount(unidentified)}
            </FilterButton>
          )}
        </div>

        {list.isLoading ? (
          <p className="px-4 py-3 text-[13px] text-[#596b85]">{t("Loading…")}</p>
        ) : list.isError ? (
          <p className="px-4 py-3 text-[13px] text-[#a23b2f]">
            {t("Could not load the objects of this module.")}
          </p>
        ) : (
          <>
            {total > items.length && (
              <p className="px-4 pt-3 text-[11px] text-[#596b85]">
                {t("Showing {{shown}} of {{total}} objects", {
                  shown: formatCount(items.length),
                  total: formatCount(total),
                })}
              </p>
            )}
            <div className="overflow-x-auto">
              <table
                data-testid="module-objects-list"
                className="w-full min-w-[720px] border-collapse text-[12px]"
              >
                <thead>
                  <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
                    <th className={HEAD}>{t("Object")}</th>
                    <th className={HEAD}>{t("Type")}</th>
                    <th className={HEAD}>{t("Module")}</th>
                    <th className={HEAD}>{t("Package")}</th>
                    <th className={HEAD}>{t("Description")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.obj_name} className="border-b border-[#eef1f5] last:border-b-0">
                      <td className={`${CELL} font-mono`}>{row.obj_name}</td>
                      <td className={`${CELL} font-mono text-[11px]`}>
                        {[row.obj_type_tadir, row.wricef].filter(Boolean).join(" · ")}
                      </td>
                      <td className={CELL}>{row.resolved_stream ?? t("No assignable module")}</td>
                      <td className={`${CELL} font-mono text-[11px]`}>{row.devclass ?? "—"}</td>
                      <td className={CELL}>{row.description_pt ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
